import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useI18n } from '../i18n/I18nContext';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import { setCoinBalance } from '../components/CoinChip';
import './BattlePassPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface BPReward {
  tier: number;
  track: 'free' | 'premium' | string;
  type: string;
  amount: number;
  code: string | null;
  label: string | null;
}

interface BPSeason {
  id: number;
  name: string;
  starts_at: string;
  ends_at: string;
  max_tier: number;
  xp_per_tier: number;
  premium_price_coins: number;
}

interface BPSeasonResponse {
  season: BPSeason;
  rewards: BPReward[];
}

interface BPProgress {
  season_id: number;
  xp: number;
  tier: number;
  max_tier: number;
  xp_per_tier: number;
  has_premium: boolean;
  premium_via_subscription: boolean;
  premium_price_coins: number;
  claimed: string[];
  ends_at: string;
}

function rewardIcon(type: string): string {
  switch (type) {
    case 'coins': return '🪙';
    case 'theme': return '🎨';
    case 'badge': return '🏅';
    case 'title': return '👑';
    case 'icon': return '🖼️';
    case 'xp_boost': return '⚡';
    default: return '🎁';
  }
}

function rewardLabel(r: BPReward, t: any): string {
  if (r.type === 'coins') return `${r.amount} 🪙`;
  if (r.label) return r.label;
  if (r.code) return r.code;
  return t.battlepass.rewardGeneric;
}

export default function BattlePassPage() {
  const { user, token } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const smartBack = useSmartBack('/');

  const [season, setSeason] = useState<BPSeason | null>(null);
  const [rewards, setRewards] = useState<BPReward[]>([]);
  const [progress, setProgress] = useState<BPProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyTier, setBusyTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const fetchSeason = useCallback(async () => {
    try {
      const r = await fetch(`${API}/battlepass/season`);
      if (!r.ok) throw new Error('season fetch failed');
      const data: BPSeasonResponse = await r.json();
      setSeason(data.season);
      setRewards(data.rewards);
    } catch (e) {
      setError(t.battlepass.loadError);
    }
  }, [t]);

  const fetchProgress = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/battlepass/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data: BPProgress = await r.json();
      setProgress(data);
    } catch {}
  }, [token]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSeason(), fetchProgress()]).finally(() => setLoading(false));
  }, [fetchSeason, fetchProgress]);

  const tierMap = useMemo(() => {
    const m = new Map<number, { free?: BPReward; premium?: BPReward }>();
    rewards.forEach(r => {
      const e = m.get(r.tier) ?? {};
      if (r.track === 'free') e.free = r;
      else if (r.track === 'premium') e.premium = r;
      m.set(r.tier, e);
    });
    return m;
  }, [rewards]);

  const tiers = useMemo(() => {
    if (!season) return [];
    return Array.from({ length: season.max_tier }, (_, i) => i + 1);
  }, [season]);

  const xpIntoTier = progress ? progress.xp % progress.xp_per_tier : 0;
  const xpProgressPct = progress ? (xpIntoTier / progress.xp_per_tier) * 100 : 0;

  const handleBuyPremium = async () => {
    if (!token) {
      navigate('/auth');
      return;
    }
    const ok = window.confirm(t.battlepass.confirmBuy.replace('{n}', String(season?.premium_price_coins ?? 1990)));
    if (!ok) return;
    setBusyTier('buy');
    try {
      const r = await fetch(`${API}/battlepass/buy-premium`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) {
        showToast((t.battlepass as any)[`error_${data.detail}`] ?? data.detail ?? t.common.error);
      } else {
        setCoinBalance(data.balance);
        await fetchProgress();
        showToast(t.battlepass.boughtToast);
      }
    } catch {
      showToast(t.common.error);
    } finally {
      setBusyTier(null);
    }
  };

  const handleClaim = async (tier: number, track: 'free' | 'premium') => {
    if (!token) {
      navigate('/auth');
      return;
    }
    setBusyTier(`${tier}:${track}`);
    try {
      const r = await fetch(`${API}/battlepass/claim/${tier}/${track}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) {
        showToast((t.battlepass as any)[`error_${data.detail}`] ?? data.detail ?? t.common.error);
      } else {
        if (data.coins_added && data.coins_added > 0) {
          setCoinBalance(data.balance);
        }
        await fetchProgress();
        showToast(t.battlepass.claimedToast);
      }
    } catch {
      showToast(t.common.error);
    } finally {
      setBusyTier(null);
    }
  };

  const claimedSet = useMemo(
    () => new Set(progress?.claimed ?? []),
    [progress]
  );

  const daysLeft = useMemo(() => {
    if (!season) return 0;
    const ms = new Date(season.ends_at).getTime() - Date.now();
    return Math.max(0, Math.floor(ms / 86400000));
  }, [season]);

  if (loading) {
    return (
      <div className="bp-page">
        <AppHeader onBack={smartBack} backLabel={t.common.back} />
        <div className="bp-loading">{t.common.loading}</div>
      </div>
    );
  }

  if (!season) {
    return (
      <div className="bp-page">
        <AppHeader onBack={smartBack} backLabel={t.common.back} />
        <div className="bp-error">{error ?? t.battlepass.loadError}</div>
      </div>
    );
  }

  return (
    <div className="bp-page">
      <AppHeader onBack={smartBack} backLabel={t.common.back} />

      <div className="bp-hero">
        <div className="bp-hero-inner">
          <div className="bp-hero-left">
            <div className="bp-hero-pill">{t.battlepass.title}</div>
            <h1 className="bp-hero-title">{season.name}</h1>
            <p className="bp-hero-sub">
              {t.battlepass.daysLeft.replace('{n}', String(daysLeft))}
            </p>
          </div>
          {progress && (
            <div className="bp-hero-right">
              <div className="bp-tier-circle">
                <span className="bp-tier-label">{t.battlepass.tier}</span>
                <span className="bp-tier-num">{progress.tier}</span>
                <span className="bp-tier-max">/ {progress.max_tier}</span>
              </div>
              <div className="bp-xp-bar-wrap">
                <div className="bp-xp-bar-bg">
                  <div className="bp-xp-bar-fill" style={{ width: `${xpProgressPct}%` }} />
                </div>
                <span className="bp-xp-text">
                  {xpIntoTier} / {progress.xp_per_tier} XP
                </span>
              </div>
            </div>
          )}
        </div>
        {progress && !progress.has_premium && (
          <>
            <button
              className="bp-buy-btn"
              onClick={handleBuyPremium}
              disabled={busyTier === 'buy'}
            >
              👑 {t.battlepass.buyPremium} · {progress.premium_price_coins} 🪙
            </button>
            <button
              className="bp-buy-btn bp-buy-btn-sub"
              onClick={() => navigate('/premium')}
            >
              ✨ {t.battlepass.buyViaSubscription}
            </button>
          </>
        )}
        {progress?.has_premium && (
          <div className="bp-premium-active">
            👑 {t.battlepass.premiumActive}
            {progress.premium_via_subscription && (
              <span className="bp-sub-badge"> ✨ {t.battlepass.subBadge}</span>
            )}
          </div>
        )}
        {!user && (
          <button className="bp-buy-btn" onClick={() => navigate('/auth')}>
            {t.battlepass.loginToTrack}
          </button>
        )}
      </div>

      <div className="bp-tiers-wrap">
        <div className="bp-tiers-header">
          <div className="bp-track-label bp-track-label--free">
            {t.battlepass.trackFree}
          </div>
          <div className="bp-track-label bp-track-label--premium">
            👑 {(t.battlepass as any)?.trackPremium ?? 'Premium'}
          </div>
        </div>

        <div className="bp-tiers-list">
          {tiers.map((tier) => {
            const entry = tierMap.get(tier);
            const free = entry?.free;
            const premium = entry?.premium;
            const userTier = progress?.tier ?? 0;
            const reached = userTier >= tier;

            const renderCell = (r: BPReward | undefined, track: 'free' | 'premium') => {
              if (!r) return <div className="bp-cell bp-cell--empty">—</div>;
              const key = `${tier}:${track}`;
              const claimed = claimedSet.has(key);
              const canClaim = reached && !claimed
                && (track === 'free' || progress?.has_premium);
              const locked = track === 'premium' && !progress?.has_premium;

              return (
                <div
                  className={[
                    'bp-cell',
                    `bp-cell--${track}`,
                    claimed ? 'bp-cell--claimed' : '',
                    canClaim ? 'bp-cell--ready' : '',
                    !reached ? 'bp-cell--locked' : '',
                  ].join(' ')}
                >
                  <div className="bp-cell-icon">{rewardIcon(r.type)}</div>
                  <div className="bp-cell-label">{rewardLabel(r, t)}</div>
                  {claimed && <div className="bp-cell-status">✓</div>}
                  {!claimed && canClaim && (
                    <button
                      className="bp-cell-claim"
                      onClick={() => handleClaim(tier, track)}
                      disabled={busyTier === key}
                    >
                      {(t.battlepass as any)?.claim ?? 'Topla'}
                    </button>
                  )}
                  {!claimed && !canClaim && locked && reached && (
                    <div className="bp-cell-status bp-cell-status--lock">🔒</div>
                  )}
                  {!reached && (
                    <div className="bp-cell-status bp-cell-status--lock">🔒</div>
                  )}
                </div>
              );
            };

            return (
              <div
                key={tier}
                className={['bp-row', reached ? 'bp-row--reached' : ''].join(' ')}
              >
                <div className="bp-tier-num-cell">{tier}</div>
                {renderCell(free, 'free')}
                {renderCell(premium, 'premium')}
              </div>
            );
          })}
        </div>
      </div>

      {toast && <div className="bp-toast">{toast}</div>}
    </div>
  );
}
